import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { usd, pct, ddmmmyy, statusTone } from "@/lib/format";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const Shipments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null); // shipment row
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    apiClient.get("/shipments", { params: { page, page_size: 20 } })
      .then(({ data }) => setData(data));
  }, [page, reloadTick]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/shipments/${pendingDelete.reference}`);
      toast.success(`Deleted ${pendingDelete.reference}`);
      setPendingDelete(null);
      setReloadTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const lastPage = Math.max(1, Math.ceil((data.total || 0) / 20));

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Records</div>
        <h1 className="text-2xl font-bold text-[#0F2044]">All shipments</h1>
      </div>

      <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="shipments-list">
        <div className="overflow-x-auto">
          <table className="dr-table w-full text-sm">
            <thead>
              <tr>
                <th>Session</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Entry</th>
                <th>Imported</th>
                <th className="text-right">Estimated</th>
                <th className="text-right">Actual</th>
                <th className="text-right">Variance</th>
                <th className="text-right">%</th>
                <th>Status</th>
                <th>Uploaded by</th>
                {isAdmin && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const tone = statusTone(r.status);
                const rowCls = tone === "match" ? "row-match"
                  : tone === "warning" ? "row-minor"
                  : tone === "error" ? "row-material"
                  : "row-review";
                return (
                  <tr key={r.reference} className={`${rowCls} cursor-pointer`}
                      onClick={() => navigate(`/shipments/${r.reference}`)}
                      data-testid={`sh-row-${r.reference}`}>
                    <td className="font-mono text-xs">{r.reference}</td>
                    <td className="font-mono text-xs">{r.invoice_number}</td>
                    <td>{r.customer_name}</td>
                    <td className="font-mono text-xs">{r.entry_number}</td>
                    <td className="uppercase-date">{ddmmmyy(r.import_date)}</td>
                    <td className="num">{usd(r.total_estimated_duty)}</td>
                    <td className="num">{usd(r.total_actual_duty)}</td>
                    <td className="num">{usd(r.variance_usd)}</td>
                    <td className="num">{pct(r.variance_pct)}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-xs">{r.uploaded_by_username}</td>
                    {isAdmin && (
                      <td className="text-right">
                        <button
                          data-testid={`delete-${r.reference}`}
                          onClick={(e) => { e.stopPropagation(); setPendingDelete(r); }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors"
                          title="Delete shipment"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!data.rows.length && (
                <tr><td colSpan={isAdmin ? 12 : 11} className="text-center text-gray-500 py-8">No shipments yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center px-4 py-3 text-xs text-gray-500" style={{ borderTop: "1px solid var(--color-border)" }}>
          <span>Page {page} of {lastPage} · {data.total} records</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              data-testid="page-prev"
              className="px-3 py-1 rounded border disabled:opacity-40"
              style={{ borderColor: "var(--color-border)" }}
            >Prev</button>
            <button
              disabled={page >= lastPage}
              onClick={() => setPage(page + 1)}
              data-testid="page-next"
              className="px-3 py-1 rounded border disabled:opacity-40"
              style={{ borderColor: "var(--color-border)" }}
            >Next</button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15, 32, 68, 0.55)" }}
          data-testid="delete-modal"
          onClick={() => !deleting && setPendingDelete(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#0F2044] mb-2">Delete shipment?</h3>
            <p className="text-sm text-gray-600 mb-1">
              You are about to permanently delete:
            </p>
            <div className="bg-slate-50 rounded-md p-3 mb-4 text-sm" style={{ border: "1px solid var(--color-border)" }}>
              <div className="font-mono text-xs text-gray-500">{pendingDelete.reference}</div>
              <div className="font-semibold text-[#0F2044]">{pendingDelete.invoice_number} · {pendingDelete.customer_name}</div>
              <div className="text-xs text-gray-500 mt-1">
                Estimated {usd(pendingDelete.total_estimated_duty)} · Actual {usd(pendingDelete.total_actual_duty)}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-5">
              This removes the shipment from all reports and dashboards. The original Excel/PDF files
              uploaded for this shipment will remain in object storage. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                data-testid="delete-cancel"
                className="px-4 py-2 rounded-md text-sm bg-white hover:bg-slate-50"
                style={{ border: "1px solid var(--color-border)" }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="delete-confirm"
                className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipments;
